import { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AuthService from "services/auth.service";
import {
  initiateMediaUpload,
  updateMediaUploadProgress,
  uploadMedia,
} from "actions/media";
import { updateWidgetProperty } from "actions/editor";
import S3Upload from "react-s3-uploader/s3upload";
import StoryboardContext from "components/Storyboard/context";
import { EditorContext } from "components/Editor";
import { toast } from "react-toastify";
import { FailureToast } from "components/Toast";
import _ from "lodash";
import { DEFAULT_MEDIA_PROPERTY } from "constants/background";

const API_URL = `${process.env.REACT_APP_PAPERFLITE_API_URL}/api/2.0`;

const DEFAULT_FALLBACK_URL =
  "https://ik.imagekit.io/zvzrxdvzjue/tr:w-1086,h-616,c-force:q-80,f-webp/5ae18247c9e77c0001c91375/images/6995719c89f8f03e5c8efc5c/8912b7a4-e833-47e7-8313-e868e778e1a4?ik-sdk-version=react-1.1.1";

const removeSpecialCharacters = (name) =>
  name.replace(/\u202F/g, "").replace(/[^\w\d_\-.\s]+/gi, "");

export async function fetchImageAsFile(imageUrl, fileName = 'image.png') {
    // First try: canvas approach (handles CORS-friendly images)
    try {
      const file = await fetchViaCanvas(imageUrl, fileName);
      return file;
    } catch (canvasError) {
      console.warn('Canvas fetch failed, trying direct fetch...', canvasError);
    }
  
    // Second try: direct fetch (works when CORS headers are present)
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
      const blob = await response.blob();
      return new File([blob], fileName, {
        type: blob.type || 'image/png',
        lastModified: Date.now(),
      });
    } catch (fetchError) {
      throw new Error(`Image load failed: ${fetchError.message}`);
    }
  }
  
  function fetchViaCanvas(imageUrl, fileName) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
  
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
  
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
  
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(new File([blob], fileName, {
              type: blob.type || 'image/png',
              lastModified: Date.now(),
            }));
          } else {
            reject(new Error('Failed to create blob'));
          }
        }, 'image/png', 0.95);
      };
  
      img.onerror = () => reject(new Error('Image load failed'));
      img.src = imageUrl;
    });
  }  

  export function useBackgroundImageUpload(widgetId, aiImageUrl) {
    const dispatch = useDispatch();
    const editorContext = useContext(EditorContext);
    const storyboardContext = useContext(StoryboardContext);
  
    const editorSession = editorContext.editorSession.id;
    const storyboardId  = _.get(storyboardContext, 'id');
  
    const { desktopWidth, desktopHeight } = useSelector((state) => {
      const widgets = _.get(state, `${editorSession}.present.widgets`, []);
      const widget  = widgets.find((w) => w.uid === widgetId);
      return {
        desktopWidth:  _.get(widget, 'properties.desktop.layout.width',  1920),
        desktopHeight: _.get(widget, 'properties.desktop.layout.height', 1080),
      };
    });
  
    const [uploadTriggered, setUploadTriggered] = useState(false);
  
    useEffect(() => {
      setUploadTriggered(false);
    }, [aiImageUrl]);
  
    useEffect(() => {
      if (!widgetId || !aiImageUrl || uploadTriggered) return;
      setUploadTriggered(true);
      triggerUpload(aiImageUrl);
    }, [aiImageUrl, uploadTriggered]);
  
    const triggerUpload = useCallback((url) => {
      if (!widgetId) return;
      fetchImageAsFile(url, 'background.png')
        .then(processAndUpload)
        .catch((err) => {
          console.warn('Background image fetch failed, trying fallback...', err);
          if (url === DEFAULT_FALLBACK_URL) {
            console.error('Background fallback also failed.');
            return;
          }
          fetchImageAsFile(DEFAULT_FALLBACK_URL, 'background.png')
            .then(processAndUpload)
            .catch((e) => console.error('Background fallback failed', e));
        });
    }, [widgetId, editorSession]);
  
    const processAndUpload = (file) => {
      if (!widgetId) return;
      const processedFile = {
        id: widgetId,
        name: file.name,
        type: file.type,
        size: file.size / (1024 * 1024),
        progress: 0,
        mediaPaste: true,
        file,
      };
  
      const updatedName = removeSpecialCharacters(processedFile.name);
  
      const s3Uploader = new S3Upload({
        signingUrl: '/file_upload/initiate',
        signingUrlMethod: 'POST',
        signingUrlHeaders: { Authorization: `Bearer ${AuthService.getAccessToken()}` },
        server: API_URL,
        scrubFilename: (filename) => removeSpecialCharacters(filename),
        signingUrlQueryParams: { originalName: encodeURIComponent(updatedName) },
        contentDisposition: 'auto',
        preprocess: (file, next) => next(file),
        uploadRequestHeaders: {},
        onFinishS3Put: (info, uploadedFile) => handleUploadComplete(info, uploadedFile),
        onProgress:    (progress) => dispatch(updateMediaUploadProgress(widgetId, progress)),
        onError: () => {
          toast(
            <FailureToast
              heading="Background Upload Failed"
              description="Unable to upload background image. Please try again."
            />,
            {
              type: toast.TYPE.DEFAULT,
              position: toast.POSITION.BOTTOM_RIGHT,
              autoClose: 3000,
              toastId: `bg-upload-error-${widgetId}-toast`,
            }
          );
        },
        files: [processedFile.file],
      });
  
      handleUploadStart(s3Uploader, processedFile);
    };
  
    const handleUploadComplete = async (info, uploadedFile) => {
      const formats = ['image/jpg','image/jpeg','image/png','image/svg','image/bmp','image/gif','image/svg+xml'];
  
      if (!info || !formats.includes(uploadedFile.type)) {
        console.warn('Background upload skipped — unsupported type:', uploadedFile.type);
        return;
      }
  
      dispatch(
        uploadMedia(
          widgetId,
          {
            fileName:    uploadedFile.name,
            contentType: uploadedFile.type,
            signedUrl:   info.signedUrl,
            width:       parseInt(desktopWidth,  10) || 1920,
            height:      parseInt(desktopHeight, 10) || 1080,
            storyboard:  storyboardId,
          },
          (response) => {
            if (!response) return;
    
            const backgroundValue = {
              ...DEFAULT_MEDIA_PROPERTY,
              type:  'image',
              media: {
                ...response,
                widgetId,
                aiImageUrl: null,
              },
            };
  
            dispatch(updateWidgetProperty(editorSession, widgetId, 'desktop', { background: backgroundValue }));
            dispatch(updateWidgetProperty(editorSession, widgetId, 'mobile',  { background: backgroundValue }));
          },
          true
        )
      );
    };
  
    const handleUploadStart = (instance, processedFile) => {
      dispatch(
        initiateMediaUpload(
          { id: processedFile.id, name: processedFile.name, type: processedFile.type, size: processedFile.size, progress: 0, instance },
          false,
          true
        )
      );
    };
  
    return { uploadTriggered };
  }